import React, { useState } from 'react';
import { Portfolio, FamilyMember, FamilyMemberForm } from '../types';
import { User } from '../services/auth';
import { 
  addFamilyMember, 
  updateFamilyMember, 
  deleteFamilyMember 
} from '../utils/apiStorage';
import { 
  calculateFamilyMemberSummary, 
  formatCurrency, 
  formatPercentage, 
  validateFamilyMemberForm 
} from '../utils/calculations';
import { Plus, Edit, Trash2, User as UserIcon, Calendar, Users, UserPlus } from 'lucide-react';

interface FamilyMembersProps {
  portfolio: Portfolio;
  setPortfolio: (portfolio: Portfolio) => void;
  hideHeader?: boolean;
  user?: User | null;
}

const FamilyMembers: React.FC<FamilyMembersProps> = ({ portfolio, setPortfolio, hideHeader = false, user }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [formData, setFormData] = useState<FamilyMemberForm>({
    name: '',
    relationship: '',
    dateOfBirth: '',
  });
  const [errors, setErrors] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateFamilyMemberForm(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editingMember) {
        // Update existing member
        const updatedMember: FamilyMember = {
          ...editingMember,
          name: formData.name.trim(),
          relationship: formData.relationship.trim(),
          dateOfBirth: formData.dateOfBirth || undefined,
        };
        
        const updatedPortfolio = await updateFamilyMember(portfolio, updatedMember);
        setPortfolio(updatedPortfolio);
      } else {
        // Add new member
        const newMember: FamilyMember = {
          name: formData.name.trim(),
          relationship: formData.relationship.trim(),
          dateOfBirth: formData.dateOfBirth || undefined,
        };
        
        const updatedPortfolio = await addFamilyMember(portfolio, newMember);
        setPortfolio(updatedPortfolio);
      }

      // Reset form
      setFormData({ name: '', relationship: '', dateOfBirth: '' });
      setShowForm(false);
      setEditingMember(null);
      setErrors([]);
    } catch (error) {
      console.error('Error saving family member:', error);
      setErrors(['Failed to save family member. Please try again.']);
    }
  };

  const handleEdit = (member: FamilyMember) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      relationship: member.relationship,
      dateOfBirth: member.dateOfBirth || '',
    });
    setShowForm(true);
    setErrors([]);
  };

  const handleDelete = async (member: FamilyMember) => {
    if (window.confirm(`Are you sure you want to delete ${member.name}? This will also remove all their investments.`)) {
      try {
        const memberId = member._id || member.id || '';
        const updatedPortfolio = await deleteFamilyMember(portfolio, memberId);
        setPortfolio(updatedPortfolio);
      } catch (error) {
        console.error('Error deleting family member:', error);
        alert('Failed to delete family member. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', relationship: '', dateOfBirth: '' });
    setShowForm(false);
    setEditingMember(null);
    setErrors([]);
  };

  const handleAddSelf = async () => {
    if (!user || !user.name) {
      setErrors(['User information not available. Please try again.']);
      return;
    }

    // Check if "Self" already exists
    const selfExists = portfolio.familyMembers.some(
      member => member.relationship === 'Self'
    );

    if (selfExists) {
      setErrors(['You have already added yourself as a family member.']);
      return;
    }

    try {
      const newMember: FamilyMember = {
        name: user.name.trim(),
        relationship: 'Self',
      };
      
      const updatedPortfolio = await addFamilyMember(portfolio, newMember);
      setPortfolio(updatedPortfolio);
      setErrors([]);
    } catch (error) {
      console.error('Error adding self:', error);
      setErrors(['Failed to add yourself. Please try again.']);
    }
  };

  const hasSelfMember = portfolio.familyMembers.some(
    member => member.relationship === 'Self'
  );

  const familyMemberSummaries = portfolio.familyMembers.map(member => 
    calculateFamilyMemberSummary(member, portfolio.investments)
  );

  return (
    <div className={hideHeader ? "space-y-6" : "p-6 space-y-6"}>
      {/* Error Display */}
      {errors.length > 0 && !showForm && (
        <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg">
          <ul className="text-sm text-danger-700 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <Users className="h-6 w-6 text-primary-600" />
              <h2 className="text-2xl font-bold text-gray-900">Family Members</h2>
            </div>
            <p className="text-gray-600">Manage your family members and their investments</p>
          </div>
          <div className="flex gap-3">
            {user && !hasSelfMember && (
              <button
                onClick={handleAddSelf}
                className="inline-flex items-center px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Self
              </button>
            )}
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Family Member
            </button>
          </div>
        </div>
      )}
      {hideHeader && (
        <div className="flex justify-end gap-3">
          {user && !hasSelfMember && (
            <button
              onClick={handleAddSelf}
              className="inline-flex items-center px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Self
            </button>
          )}
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Family Member
          </button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingMember ? 'Edit Family Member' : 'Add New Family Member'}
            </h3>
            
            {errors.length > 0 && (
              <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg">
                <ul className="text-sm text-danger-700 space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relationship *
                </label>
                <select
                  value={formData.relationship}
                  onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select relationship</option>
                  <option value="Self">Self</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Child">Child</option>
                  <option value="Parent">Parent</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  {editingMember ? 'Update' : 'Add'} Member
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Family Members List */}
      {portfolio.familyMembers.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Family Members</h3>
          <p className="text-gray-600 mb-6">
            Start by adding yourself, then add other family members to manage their investments
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {user && !hasSelfMember && (
              <button
                onClick={handleAddSelf}
                className="inline-flex items-center justify-center px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Self
              </button>
            )}
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Family Member
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {familyMemberSummaries.map((memberSummary) => {
            const member = memberSummary.familyMember;
            const investmentCount = portfolio.investments.filter(
              inv => inv.familyMemberId === member.id
            ).length;

            return (
              <div key={member.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <UserIcon className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
                      <p className="text-sm text-gray-600">{member.relationship}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(member)}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(member)}
                      className="p-2 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {member.dateOfBirth && (
                  <div className="flex items-center text-sm text-gray-600 mb-4">
                    <Calendar className="w-4 h-4 mr-2" />
                    Born: {new Date(member.dateOfBirth).toLocaleDateString()}
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Investments</span>
                    <span className="text-sm font-medium text-gray-900">{investmentCount}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Invested</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(memberSummary.totalInvested)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Current Value</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(memberSummary.totalCurrentValue)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Growth</span>
                    <span className={`text-sm font-medium ${
                      memberSummary.growthPercentage >= 0 ? 'text-success-600' : 'text-danger-600'
                    }`}>
                      {formatPercentage(memberSummary.growthPercentage)}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-xs text-gray-500">
                    Added: {member.createdAt ? new Date(member.createdAt).toLocaleDateString() : 'Unknown'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FamilyMembers;
